import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthCard from '../components/AuthCard';
import { authStyles } from '../styles/authStyles';

export default function TptArrivePage() {
  const { teacher, loading: authLoading, tptArrive, tptUnlock, isLightweightAccount } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const novelSlug = searchParams.get('novel');

  const [classroomName, setClassroomName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unlockedNovel, setUnlockedNovel] = useState(null);

  // Prevent re-entry: tptUnlock calls fetchMe() and tptArrive sets the new
  // teacher, either of which would re-trigger this effect without the guard.
  const unlockCalledRef = useRef(false);

  // If a logged-in teacher (full or lightweight) arrives via UTM, unlock the
  // novel without showing the form. Spec §9.
  useEffect(() => {
    if (authLoading || !teacher || !novelSlug) return;
    if (unlockCalledRef.current) return;
    unlockCalledRef.current = true;

    setLoading(true);
    tptUnlock(novelSlug)
      .then((data) => setUnlockedNovel(data.unlocked_novel))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [authLoading, teacher]); // eslint-disable-line

  // Still checking auth state
  if (authLoading) return null;

  if (!novelSlug) {
    const fromTpt = searchParams.get('utm_source') === 'tpt';
    return (
      <AuthCard>
        {fromTpt ? (
          <>
            <h1 style={authStyles.heading}>Have a TpT printable?</h1>
            <p style={styles.sub}>
              Great! Open the PDF you downloaded from Teachers Pay Teachers and click the
              link inside it — it includes a code that unlocks your novel here for free.
            </p>
            <p style={{ ...styles.sub, marginTop: '-12px' }}>
              Don't have a TpT printable yet?{' '}
              <Link to="/signup" style={authStyles.link}>Subscribe for full library access</Link>
              {' '}instead.
            </p>
            <Link to="/" style={styles.button}>Back to homepage</Link>
          </>
        ) : (
          <>
            <h1 style={authStyles.heading}>Link not recognised</h1>
            <p style={styles.sub}>
              We couldn't match this link to a novel. Please check the link in your TpT PDF
              download, or{' '}
              <a href="mailto:hello@classroom.bingo" style={authStyles.link}>contact support</a>.
            </p>
            <Link to="/" style={styles.button}>Go to homepage</Link>
          </>
        )}
      </AuthCard>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // tptArrive unlocks the novel itself; stop the effect from handling the new teacher
    unlockCalledRef.current = true;
    try {
      const result = await tptArrive(classroomName, novelSlug);
      setUnlockedNovel(result.unlocked_novel);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-unlock in progress for logged-in teacher
  if (loading && teacher) {
    return (
      <AuthCard>
        <p style={styles.sub}>Unlocking your novel…</p>
      </AuthCard>
    );
  }

  // Confirmation screen — shown after new lightweight account creation
  // or after auto-unlock for a logged-in full account
  if (unlockedNovel) {
    const isFullAccount = teacher && !isLightweightAccount;
    return (
      <AuthCard>
        <div style={styles.successIcon}>✓</div>
        <h1 style={authStyles.heading}>You're all set!</h1>
        <p style={styles.sub}>
          <strong>{unlockedNovel.title}</strong> is permanently unlocked on your account.
          You can play unlimited games with this novel for free — no expiry.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ ...styles.button, display: 'block', textAlign: 'center', textDecoration: 'none' }}
        >
          Go to my dashboard →
        </button>
        {!isFullAccount && (
          <div style={styles.upgradeNote}>
            Want access to the full library?{' '}
            <Link to="/signup?upgrade=true" style={authStyles.link}>Create a full account</Link>
            {' '}and subscribe for $19.99/yr.
          </div>
        )}
      </AuthCard>
    );
  }

  // Logged-in teacher but auto-unlock failed (e.g. invalid slug)
  if (teacher && error) {
    return (
      <AuthCard>
        <h1 style={authStyles.heading}>Link not recognised</h1>
        <p style={styles.sub}>
          {error}
        </p>
        <button onClick={() => navigate('/dashboard')} style={styles.button}>
          Go to my dashboard →
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <h1 style={authStyles.heading}>Welcome!</h1>
      <p style={styles.sub}>
        Your TpT printable includes free access to the online version of this novel.
        Enter your classroom name to get started.
      </p>

      {error && <div style={authStyles.error}>{error}</div>}

      <form onSubmit={handleSubmit} style={authStyles.form}>
        <label style={authStyles.label}>
          Classroom name
          <input
            type="text"
            value={classroomName}
            onChange={(e) => setClassroomName(e.target.value)}
            style={authStyles.input}
            required
            placeholder="e.g. Ms. Johnson's 5th Grade"
            autoFocus
          />
          <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
            Shown at the top of student bingo boards
          </span>
        </label>
        <button type="submit" disabled={loading} style={styles.button}>
          {loading ? 'Setting up…' : 'Get started →'}
        </button>
      </form>

      <div style={authStyles.footer}>
        Already have an account?{' '}
        <Link to="/login" style={authStyles.link}>Log in</Link>
      </div>
    </AuthCard>
  );
}

// Page-specific overrides/additions on top of ../styles/authStyles
const styles = {
  successIcon: {
    width: '48px', height: '48px', backgroundColor: '#dcfce7', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '24px', color: '#16a34a', marginBottom: '16px',
  },
  sub: { ...authStyles.sub, lineHeight: '1.6' },
  button: { ...authStyles.button, marginTop: '4px' },
  upgradeNote: { textAlign: 'center', fontSize: '13px', color: '#6b6b7a', marginTop: '16px', lineHeight: '1.5' },
};
